import { Flex } from "@chakra-ui/react";
import { useQueries, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { range } from "lodash";
import { ComponentProps, useCallback, useMemo, useState } from "react";
import {
  TaskElement,
  useAllTasks,
} from "../../hooks/api/karl-farm/useAllTasks";
import {
  EditTaskRequestBody,
  editTaskQueryData,
  useEditTaskMutation,
} from "../../hooks/api/karl-farm/useEditTaskMutation";
import { useFields } from "../../hooks/api/karl-farm/useFields";
import { useMachines } from "../../hooks/api/karl-farm/useMachines";
import {
  Score,
  getUseScoresQueryFunction,
  getUseScoresQueryKey,
  scoresStaleTime,
} from "../../hooks/api/karl-farm/useScores";
import { useUsers } from "../../hooks/api/karl-farm/useUsers";
import { useAuthGuard } from "../../hooks/useAuthGuard";
import { useFieldGroups } from "../../hooks/useFieldGroups";
import { useTaskToCalendarTask } from "../../hooks/useTaskToCalendarTask";
import { getScoreColor } from "../../utils/getScoreColor";
import { getSkewedScore } from "../../utils/getSkewedScore";
import { isNonNullable } from "../../utils/typeGuards";
import { Calendar } from "../CalendarTemplate/Calendar/Calendar";
import {
  useColorTasksPerAssiciatedUsers,
  useDaysToDisplay,
  useFieldGroupsFilter,
  useFieldsFilter,
  useMachinesFilter,
  useSelectedDate,
  useShowHeatMaps,
  useStartCalendarOnSelectedDate,
  useTasksFilter,
  useUsersFilter,
} from "../CalendarTemplate/storageHooks";
import { Drawer } from "../Drawer";
import { WithSideNavigation } from "../WithSideNavigation/WithSideNavigation";
import { AddTaskDrawer } from "./AddTaskDrawer";
import { CalendarPageHeader } from "./CalendarPageHeader";
import { EditTaskDrawer } from "./EditTaskDrawer/EditTaskDrawer";

const calcTaskBody = (
  task: TaskElement,
  start: Date,
  end: Date
): EditTaskRequestBody => {
  return {
    time: {
      start: start.getTime() / 1000,
      duration: (end.getTime() - start.getTime()) / 1000 / 60,
    },
    attributes: {
      ...task.attributes,
      title: task.attributes.title || "",
    },
    fields: task.fields.map((f) => ({ id: f.id })),
    machines: task.machines.map((m) => ({ id: m.id })),
    users: task.users.map((u) => ({ id: u.id })),
  };
};

export const CalendarTemplate: React.FC = () => {
  useAuthGuard();

  const { data: fieldsData } = useFields(); // Don't remove if unused. Preloads fields for use in task drawer too.
  const { data: usersData } = useUsers(); // Don't remove if unused. Preloads fields for use in filters popover too.
  const { data: machinesData } = useMachines(); // Don't remove if unused. Preloads fields for use in filters popover too.
  const fieldGroupsData = useFieldGroups(fieldsData);

  const { value: rawFieldGroupsFilter } = useFieldGroupsFilter();
  const fieldGroupsFilter = useMemo(
    () => rawFieldGroupsFilter ?? [],
    [rawFieldGroupsFilter]
  );

  const { value: rawTasksFilter } = useTasksFilter();
  const tasksFilter = useMemo(() => rawTasksFilter ?? [], [rawTasksFilter]);

  const { value: rawFieldsFilter } = useFieldsFilter();
  const fieldsFilter = useMemo(() => rawFieldsFilter ?? [], [rawFieldsFilter]);

  const { value: rawUsersFilter } = useUsersFilter();
  const usersFilter = useMemo(() => rawUsersFilter ?? [], [rawUsersFilter]);

  const { value: rawMachinesFilter } = useMachinesFilter();
  const machinesFilter = useMemo(
    () => rawMachinesFilter ?? [],
    [rawMachinesFilter]
  );

  const startOfToday = dayjs().startOf("day").toDate();
  const defaultSelectedDate = startOfToday;
  const { value: selectedDate, set: setSelectedDate } =
    useSelectedDate(defaultSelectedDate);

  const { value: showHeatMaps, set: setShowHeatMaps } = useShowHeatMaps();

  const {
    value: colorTasksPerAssociatedUsers,
    set: setColorTasksPerAssociatedUsers,
  } = useColorTasksPerAssiciatedUsers();

  const {
    value: startCalendarOnSelectedDate,
    set: setStartCalendarOnSelectedDate,
  } = useStartCalendarOnSelectedDate();

  const { value: unsafeDaysToDisplay } = useDaysToDisplay();
  const daysToDisplayString = unsafeDaysToDisplay || "7";
  const daysToDisplay = parseInt(daysToDisplayString);

  const startOfSelectedWeek = dayjs(selectedDate).startOf("week").toDate();
  const startDate =
    !startCalendarOnSelectedDate &&
    (daysToDisplayString === "7" || daysToDisplayString === "10")
      ? startOfSelectedWeek
      : selectedDate || defaultSelectedDate;

  const endDate = useMemo(() => {
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + daysToDisplay - 1);
    endDate.setHours(23, 59, 59, 999);
    return endDate;
  }, [daysToDisplay, startDate]);

  const [shownDrawer, setShownDrawer] = useState<
    "addTaskDrawer" | "editTaskDrawer" | null
  >(null);

  const { data: tasksData } = useAllTasks();

  const filteredTasks = useMemo(() => {
    const tasksToFilter = tasksData?.data?.tasks || [];
    const inDateRangeFilter = (task: TaskElement): boolean => {
      const taskStartMillis = task.time.start * 1000;
      const taskDurationMillis = task.time.duration * 60 * 1000;
      const taskStartDate = new Date(taskStartMillis);
      const taskEndDate = new Date(taskStartMillis + taskDurationMillis);
      return startDate < taskEndDate && taskStartDate < endDate;
    };

    const inTasksFilter = (task: TaskElement): boolean => {
      if (tasksFilter.length === 0) return true;
      console.log(tasksFilter);
      console.log(task.attributes.task);

      return tasksFilter.includes(task.attributes.task);
    };

    const inFieldGroupsFilter = (task: TaskElement): boolean => {
      if (fieldGroupsFilter.length === 0) return true;
      const taskFieldGroups = task.fields
        .map((field) => {
          return fieldsData?.data?.fields.find((f) => f.id === field.id)
            ?.custom_attributes?.app_properties?.farm_name; // farm_name hasn't been renamed to field_group in the backend yet
        })
        .filter(isNonNullable);
      return taskFieldGroups.some((fieldGroup) =>
        fieldGroupsFilter.includes(fieldGroup)
      );
    };
    const inFieldsFilter = (task: TaskElement): boolean => {
      if (fieldsFilter.length === 0) return true;
      return task.fields.some((field) => fieldsFilter.includes(field.id));
    };
    const inUsersFilter = (task: TaskElement): boolean => {
      if (usersFilter.length === 0) return true;
      return task.users.some((user) => usersFilter.includes(user.id));
    };
    const inMachinesFilter = (task: TaskElement): boolean => {
      if (machinesFilter.length === 0) return true;
      return task.machines.some((machine) =>
        machinesFilter.includes(machine.id)
      );
    };
    return tasksToFilter.filter(
      (task) =>
        inDateRangeFilter(task) &&
        inFieldGroupsFilter(task) &&
        inFieldsFilter(task) &&
        inUsersFilter(task) &&
        inMachinesFilter(task) &&
        inTasksFilter(task)
    );
  }, [
    tasksData?.data?.tasks,
    startDate,
    endDate,
    fieldsData?.data?.fields,
    fieldGroupsFilter,
    fieldsFilter,
    usersFilter,
    machinesFilter,
    tasksFilter,
  ]);

  const tasksScores = useQueries({
    queries: filteredTasks.map((task) => {
      const params: Parameters<typeof getUseScoresQueryKey> = [
        task.fields[0]?.id,
        startDate.getTime() / 1000,
        task.time.duration,
        task.attributes.task,
        task.attributes.crop,
        task.attributes.tilling_depth,
        task.attributes.drop_size,
      ];
      return {
        queryKey: getUseScoresQueryKey(...params),
        queryFn: getUseScoresQueryFunction(...params),
        enabled: task.attributes.task !== "other" && task.fields[0] != null,
        staleTime: scoresStaleTime,
      };
    }),
  });

  const taskIdToScoreMap = useMemo(() => {
    return filteredTasks.reduce(
      (taskIdToScoreMap, currentTask, i) => ({
        ...taskIdToScoreMap,
        [currentTask.id]: tasksScores[i], // Assume that the order of the tasks is the same as the order of the scores
      }),
      {} as Record<string, (typeof tasksScores)[number]>
    );
  }, [tasksScores, filteredTasks]);

  const [editTaskId, setEditTaskId] = useState<string | null>(null);
  const editTask = filteredTasks.find((t) => t.id === editTaskId) || null;

  const getScoresArrayOffset = useCallback(
    (scores: Score[]): number => {
      const firstTimeSlotSeconds =
        new Date(scores[0].timestamp * 1000).getTime() / 1000;
      const offsetInSeconds = firstTimeSlotSeconds - startDate.getTime() / 1000;
      const offsetInHours = offsetInSeconds / 60 / 60;
      return offsetInHours;
    },
    [startDate]
  );

  const taskToCalendarTask = useTaskToCalendarTask();

  const calendarTasks: ComponentProps<typeof Calendar>["tasks"] = useMemo(
    () =>
      filteredTasks.map((task) =>
        taskToCalendarTask(
          task,
          fieldsData?.data?.fields || null,
          taskIdToScoreMap[task.id]
        )
      ),
    [
      fieldsData?.data?.fields,
      filteredTasks,
      taskIdToScoreMap,
      taskToCalendarTask,
    ]
  );

  const handlePreviousClick = () => {
    const newDate = new Date(startDate);
    newDate.setDate(newDate.getDate() - daysToDisplay);
    setSelectedDate(newDate);
  };

  const handleNextClick = () => {
    const newDate = new Date(startDate);
    newDate.setDate(newDate.getDate() + daysToDisplay);
    setSelectedDate(newDate);
  };

  const [addTaskStartDate, setAddTaskStartDate] = useState<Date | null>(null);
  const handleEmptyTimeSlotClick = (start: Date) => {
    setAddTaskStartDate(start);
    setShownDrawer("addTaskDrawer");
    setEditTaskId(null);
  };

  const handleTaskClick = (taskId: string) => {
    const task = filteredTasks.find((task) => task.id === taskId);
    setEditTaskId(task?.id || null);
    setShownDrawer("editTaskDrawer");
  };

  const { mutate } = useEditTaskMutation();

  const handleTaskDrag = (taskId: string, start: Date, end: Date) => {
    const task = filteredTasks.find((task) => task.id === taskId);
    if (!task) return;
    editTaskQueryData(taskId, calcTaskBody(task, start, end));
  };

  const handleTaskDragEnd = (taskId: string, start: Date, end: Date) => {
    const task = filteredTasks.find((task) => task.id === taskId);
    if (!task) return;
    mutate({ id: taskId, body: calcTaskBody(task, start, end) });
  };

  type ActiveScoresQueryParams = Parameters<typeof getUseScoresQueryKey>;
  const activeScoresQueryParams: ActiveScoresQueryParams = useMemo(() => {
    return editTask == null
      ? ([] as unknown as ActiveScoresQueryParams) // Type assertion is okay, as the query will not be called unless editTask is not null
      : ([
          editTask.fields[0]?.id,
          startDate.getTime() / 1000,
          editTask.time.duration,
          editTask.attributes.task,
          editTask.attributes.crop,
          editTask.attributes.tilling_depth,
          editTask.attributes.drop_size,
        ] as ActiveScoresQueryParams);
  }, [editTask, startDate]);
  const { data: activeScoresData } = useQuery(
    getUseScoresQueryKey(...activeScoresQueryParams),
    getUseScoresQueryFunction(...activeScoresQueryParams),
    {
      enabled:
        editTask != null &&
        editTask.attributes.task !== "other" &&
        editTask.fields[0] != null,
      staleTime: scoresStaleTime,
    }
  );

  const timeSlotColors = useMemo(() => {
    const scores = activeScoresData?.data;
    if (scores == null) return [];
    return range(daysToDisplay).map((day) =>
      range(24).map((hour) => {
        const offsetInHours = getScoresArrayOffset(scores);
        const score = scores[day * 24 + hour - offsetInHours]?.value;
        return score != null
          ? getScoreColor(getSkewedScore(score), true)
          : "hsl(60, 100%, 100%)";
      })
    );
  }, [activeScoresData?.data, daysToDisplay, getScoresArrayOffset]);

  return (
    <WithSideNavigation>
      <Flex maxHeight="100%" direction="row" justifyContent="stretch">
        <Flex
          flex="1"
          padding="40px"
          flexDirection="column"
          gap="30px"
          overflowY="auto"
        >
          <CalendarPageHeader
            startDate={startDate}
            selectedDate={selectedDate}
            onMantineCalendarChange={(date) => {
              if (date != null) setSelectedDate(date);
            }}
            onPreviousClick={handlePreviousClick}
            onTodayClick={() =>
              setSelectedDate(dayjs(new Date()).startOf("day").toDate())
            }
            onNextClick={handleNextClick}
            showHeatMaps={showHeatMaps}
            onShowHeatMapsToggle={() => {
              setShowHeatMaps((prev) => !prev);
            }}
            colorTasksPerAssociatedUsers={colorTasksPerAssociatedUsers}
            onColorTasksPerAssociatedUsersToggle={() => {
              setColorTasksPerAssociatedUsers((prev) => !prev);
            }}
            startCalendarOnSelectedDate={startCalendarOnSelectedDate}
            onStartCalendarOnSelectedDateToggle={() => {
              setStartCalendarOnSelectedDate((prev) => !prev);
            }}
            fieldGroupsData={fieldGroupsData}
            fieldsData={fieldsData}
            usersData={usersData}
            machinesData={machinesData}
            onAddTaskClick={() => {
              setAddTaskStartDate(null);
              setShownDrawer("addTaskDrawer");
              setEditTaskId(null);
            }}
            taskSelected={editTask != null}
          />

          <Calendar
            daysToDisplay={parseInt(daysToDisplayString)}
            startDate={startDate}
            endDate={endDate}
            tasks={calendarTasks}
            selectedTaskId={editTaskId}
            timeSlotColors={showHeatMaps ? timeSlotColors : []}
            onEmptyTimeSlotClick={handleEmptyTimeSlotClick}
            onTaskClick={handleTaskClick}
            onTaskDrag={handleTaskDrag}
            onTaskDragEnd={handleTaskDragEnd}
          />
        </Flex>
        <Drawer
          isOpen={
            shownDrawer === "addTaskDrawer" ||
            (shownDrawer === "editTaskDrawer" && editTask != null)
          }
          openSize="360px"
        >
          {shownDrawer === "addTaskDrawer" ? (
            <AddTaskDrawer
              start={addTaskStartDate}
              onClose={() => {
                setShownDrawer(null);
              }}
            />
          ) : shownDrawer === "editTaskDrawer" ? (
            <EditTaskDrawer
              calendarStartDate={startDate}
              task={editTask}
              onClose={() => {
                setShownDrawer(null);
                setEditTaskId(null);
              }}
            />
          ) : null}
        </Drawer>
      </Flex>
    </WithSideNavigation>
  );
};
